import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { MultiDocumentUploaderProvider } from '@/modules/documents/MultiDocumentUploader/context/MultiDocumentUploader.provider';

import { DocumentBackgroundPersister } from '../components/DocumentBackgroundPersister';
import {
  DocumentUploadStepContext,
  DocumentUploadStepContextValue,
} from './documentUploadStep.context';

/**
 * Wraps the "Documents" step in the shared document-uploader context plus a
 * thin UI-state context.
 *
 * The wizard mounts this with a per-open-session `key`, so each time the modal
 * opens it starts from a fresh instance — discarding the previous session's
 * uploaded files and persistence flag, and neutralizing any late-arriving
 * async callbacks from a prior session (their setState targets an unmounted
 * instance). The key is stable within a session, so moving between steps never
 * remounts the Modal shell.
 */
interface DocumentUploadStepProviderProps extends PropsWithChildren {
  /** Whether the onboarding modal is open. Drives the "has been closed" latch. */
  isOpen: boolean;
  householdId: string | null;
  /** The wizard's close-and-reset handler, used by actions that leave the flow. */
  onCloseWizard: () => void;
}

export function DocumentUploadStepProvider({
  isOpen,
  householdId,
  onCloseWizard,
  children,
}: DocumentUploadStepProviderProps) {
  const [isRecommendationsExpanded, setIsRecommendationsExpanded] =
    useState(false);

  // Pin the last non-null householdId for the life of the session. Closing the
  // wizard resets the prop to null, but a committed save that resolves after
  // close (the background persister finishing an upload that was still in
  // flight when the user advanced) must still target the household created in
  // step 1 — otherwise persist() would early-return on a null id and drop those
  // documents. Only ever set from null -> id within a session (the provider is
  // keyed on open, so it starts null again next time).
  const householdIdRef = useRef(householdId);
  if (householdId) householdIdRef.current = householdId;
  const sessionHouseholdId = householdIdRef.current;

  // Latches once the modal closes this session, so an async continuation that
  // resolves during/after the close doesn't act on the reset wizard. Reset per
  // open-session because the provider is keyed on open. Set during render (not
  // in a passive effect) so the latch is accurate the moment the closed state
  // commits — an effect runs after paint, leaving a window where a persist can
  // resolve and still see a stale `false`. Monotonic (only ever false -> true)
  // and never read during render, so the render-time write is safe.
  const hasBeenClosedRef = useRef(false);
  if (!isOpen) hasBeenClosedRef.current = true;
  const hasBeenClosed = useCallback(() => hasBeenClosedRef.current, []);

  // Per-file persistence claims shared by every persist caller (the footer's
  // Continue, the background persister, and the save-later action).
  // `uploadDocuments` is non-idempotent, so a file is claimed synchronously
  // before its save and only released on failure — a concurrent caller then
  // skips already-claimed files, so the same file is never created twice, while
  // files uploaded later can still be claimed and saved on a subsequent persist.
  const claimedFileIdsRef = useRef<Set<string>>(new Set());
  const claimForPersist = useCallback((fileIds: string[]): string[] => {
    const newlyClaimed = fileIds.filter(
      (id) => !claimedFileIdsRef.current.has(id)
    );
    newlyClaimed.forEach((id) => claimedFileIdsRef.current.add(id));
    return newlyClaimed;
  }, []);
  const releaseClaim = useCallback((fileIds: string[]) => {
    fileIds.forEach((id) => claimedFileIdsRef.current.delete(id));
  }, []);

  // Tracks in-flight saves so a caller that leaves the flow on success can wait
  // for concurrent saves (e.g. the background persister's) to finish first —
  // claims are synchronous, so without this a caller would see "nothing to
  // save" and navigate away while another save is still pending and could fail.
  const inFlightPersistsRef = useRef<Set<Promise<boolean>>>(new Set());
  const trackPersist = useCallback((run: Promise<boolean>) => {
    inFlightPersistsRef.current.add(run);
    void run.finally(() => inFlightPersistsRef.current.delete(run));
  }, []);
  const awaitInFlightPersists = useCallback(async (): Promise<boolean> => {
    const pending = Array.from(inFlightPersistsRef.current);
    if (pending.length === 0) return true;
    const results = await Promise.all(pending);
    return results.every(Boolean);
  }, []);

  const persistedDocumentIdsRef = useRef<Set<string>>(new Set());
  const recordPersistedDocumentIds = useCallback((documentIds: string[]) => {
    documentIds.forEach((id) => persistedDocumentIdsRef.current.add(id));
  }, []);
  const getPersistedDocumentIds = useCallback(
    () => Array.from(persistedDocumentIdsRef.current),
    []
  );

  // Latches once the user commits (Continue / an exit). The background
  // persister only saves after this, so uploads the user abandons by closing
  // the wizard beforehand never become orphan Documents.
  const hasCommittedRef = useRef(false);
  const markCommitted = useCallback(() => {
    hasCommittedRef.current = true;
  }, []);
  const hasCommitted = useCallback(() => hasCommittedRef.current, []);

  const toggleRecommendations = useCallback(
    () => setIsRecommendationsExpanded((prev) => !prev),
    []
  );
  const expandRecommendations = useCallback(
    () => setIsRecommendationsExpanded(true),
    []
  );

  const value = useMemo<DocumentUploadStepContextValue>(
    () => ({
      householdId: sessionHouseholdId,
      closeWizard: onCloseWizard,
      hasBeenClosed,
      isRecommendationsExpanded,
      toggleRecommendations,
      expandRecommendations,
      claimForPersist,
      releaseClaim,
      trackPersist,
      awaitInFlightPersists,
      recordPersistedDocumentIds,
      getPersistedDocumentIds,
      markCommitted,
      hasCommitted,
    }),
    [
      sessionHouseholdId,
      onCloseWizard,
      hasBeenClosed,
      isRecommendationsExpanded,
      toggleRecommendations,
      expandRecommendations,
      claimForPersist,
      releaseClaim,
      trackPersist,
      awaitInFlightPersists,
      recordPersistedDocumentIds,
      getPersistedDocumentIds,
      markCommitted,
      hasCommitted,
    ]
  );

  return (
    <MultiDocumentUploaderProvider>
      <DocumentUploadStepContext.Provider value={value}>
        {/* Always mounted for the session (not tied to the current step), so
            uploads still in flight when the user advances finish and persist in
            the background. */}
        <DocumentBackgroundPersister />
        {children}
      </DocumentUploadStepContext.Provider>
    </MultiDocumentUploaderProvider>
  );
}
